import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';

import { BiensImmobs } from 'app/models/biensImmobs';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { BiensImmobsBiensImmobs } from 'app/services/biens-immobs/biens-immobs.service';
import { AwscontrolService, UploadResponse, DeleteResponse } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-modifier-biens-immob',
  templateUrl: './modifier-biens-immob.component.html',
  styleUrls: ['./modifier-biens-immob.component.scss']
})
export class ModifierBiensImmobComponent implements OnInit {
  @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;

  // Form data
  biens: BiensImmobs;
  
  // File upload
  selectedFiles: FileList;
  promoSelectedFiles: FileList;
  
  // Images
  public allImages: string[] = [];
  public imagePromo: string = '';
  
  // Agencies
  selectedAgencyName: string = '';
  
  // Agency search functionality
  public agencySearch: string = '';
  public filteredAgencies: any[] = [];
  public allAgencies: any[] = [];
  public isAgencySelectOpen: boolean = false;
  
  // Categories
  public categoryInput: string = '';
  
  // Other
  public biensImmob: any;
  public idBienImmobs: any;
  dataSource = new MatTableDataSource();

  // Upload states
  public isUploading: boolean = false;
  public isUploadingPromo: boolean = false;
  public uploadProgress: number = 0;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _location: Location,
    private agenciesService: AgenciesService,
    private bienImmobService: BiensImmobsBiensImmobs,
    private awsService: AwscontrolService // Add AWS service
  ) {
    this.biens = new BiensImmobs();
    this.selectedFiles = null!;
    this.promoSelectedFiles = null!;
  }

  ngOnInit(): void {
    this.idBienImmobs = this.activatedRoute.snapshot.paramMap.get('id');
    this.getData();
    this.loadAgencies();
  }

  getData() {
    this.bienImmobService.getBiensImmobs(this.idBienImmobs).subscribe(data => {
      console.log('Bien data loaded:', data);
      this.biens = data;
      
      // Initialize images
      if (data.imageUrl) {
        if (Array.isArray(data.imageUrl)) {
          this.allImages = [...data.imageUrl];
        } else if (typeof data.imageUrl === 'string') {
          this.allImages = [data.imageUrl];
        }
      }
      
      // Initialize agency name
      if (data.agencyName) {
        if (typeof data.agencyName === 'object') {
          const agencyObj = data.agencyName as any;
          this.selectedAgencyName = agencyObj.name || '';
        } else {
          this.selectedAgencyName = data.agencyName;
        }
      }
      
      // Initialize categories
      if (!this.biens.categoryList) {
        this.biens.categoryList = [];
      }
      
      // Initialize promo image
      if (this.biens.promoImg) {
        this.imagePromo = this.biens.promoImg;
      }
      
      // Set type and role from existing data
      if (!this.biens.type && data.type) {
        this.biens.type = data.type;
      }
      
      if (!this.biens.role && data.role) {
        this.biens.role = data.role;
      }
      
      console.log('Property loaded successfully:', this.biens);
    });
  }

  loadAgencies() {
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        if (Array.isArray(data)) {
          this.allAgencies = data
            .filter(agency => agency && agency.name && typeof agency.name === 'string' && (agency.role =='meilleurs-biens' || agency.role =='agence-immobilieres'))
            .map(agency => ({
              id: agency.id,
              name: agency.name.trim()
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
        } else if (data && typeof data === 'object' && data.name) {
          this.allAgencies = [{
            id: data.id,
            name: data.name.trim()
          }];
        } else {
          this.allAgencies = [];
        }
        
        this.filteredAgencies = [...this.allAgencies];
      },
      error: (error) => {
        console.error('Error loading agencies:', error);
        this.allAgencies = [];
        this.filteredAgencies = [];
      }
    });
  }

  // Text formatting
  formatText(command: string): void {
    if (this.descriptionTextarea) {
      const textarea = this.descriptionTextarea.nativeElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = this.biens.description.substring(start, end);
      
      let formattedText = '';
      switch (command) {
        case 'bold':
          formattedText = `<strong>${selectedText}</strong>`;
          break;
        case 'italic':
          formattedText = `<em>${selectedText}</em>`;
          break;
        case 'underline':
          formattedText = `<u>${selectedText}</u>`;
          break;
      }
      
      const newText = this.biens.description.substring(0, start) + 
                     formattedText + 
                     this.biens.description.substring(end);
      
      this.biens.description = newText;
      
      // Set focus back to textarea
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
      }, 0);
    }
  }

  // Categories management
  addCategory(): void {
    if (this.categoryInput && this.categoryInput.trim() !== '') {
      if (!this.biens.categoryList) {
        this.biens.categoryList = [];
      }
      this.biens.categoryList.push(this.categoryInput.trim());
      this.categoryInput = '';
    }
  }

  removeCategory(category: string): void {
    if (this.biens.categoryList) {
      const index = this.biens.categoryList.indexOf(category);
      if (index >= 0) {
        this.biens.categoryList.splice(index, 1);
      }
    }
  }

  // Agency search
  filterAgencies(): void {
    if (!this.agencySearch || this.agencySearch.trim() === '') {
      this.filteredAgencies = [...this.allAgencies];
    } else {
      const searchTerm = this.agencySearch.toLowerCase().trim();
      this.filteredAgencies = this.allAgencies.filter(agency => {
        if (!agency || !agency.name) return false;
        return agency.name.toLowerCase().includes(searchTerm);
      });
    }
  }

  clearAgencySearch(): void {
    this.agencySearch = '';
    this.filteredAgencies = [...this.allAgencies];
  }

  onAgencySelectOpened() {
    this.isAgencySelectOpen = true;
    this.agencySearch = '';
    this.filterAgencies();
  }

  onAgencySelectClosed() {
    this.isAgencySelectOpen = false;
  }

  // Main images upload (updated with AWS service)
  selectFileimg(e: any) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const totalImages = this.allImages.length + files.length;
    if (totalImages > 20) {
      Swal.fire({
        icon: 'warning',
        title: 'Trop d\'images',
        text: `Maximum 20 images autorisées. Vous avez déjà ${this.allImages.length} images.`
      });
      return;
    }
    
    this.uploadMultipleImages(files);
    e.target.value = ''; // Reset input
  }

  async uploadMultipleImages(files: FileList): Promise<void> {
    this.isUploading = true;
    this.uploadProgress = 0;
    
    const totalFiles = files.length;
    let uploadedCount = 0;
    
    // Show progress dialog
    Swal.fire({
      title: 'Téléchargement en cours...',
      html: `0/${totalFiles} images téléchargées`,
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    try {
      for (let i = 0; i < totalFiles; i++) {
        const file = files[i];
        
        // File size validation
        if (file.size > 5 * 1024 * 1024) {
          Swal.fire({
            icon: 'warning',
            title: 'Fichier trop volumineux',
            text: `L'image ${file.name} dépasse 5MB. Taille maximum autorisée: 5MB.`
          });
          continue;
        }
        
        // Upload single image
        await this.uploadSingleImage(file);
        
        // Update progress
        uploadedCount++;
        this.uploadProgress = Math.round((uploadedCount / totalFiles) * 100);
        
        // Update progress in dialog
        Swal.update({
          html: `${uploadedCount}/${totalFiles} images téléchargées (${this.uploadProgress}%)`
        });
      }
      
      Swal.close();
      Swal.fire({
        icon: 'success',
        title: 'Téléchargement terminé',
        text: `${uploadedCount} images ont été téléchargées avec succès`,
        timer: 2000,
        showConfirmButton: false
      });
      
    } catch (error) {
      console.error('Error uploading images:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Erreur de téléchargement',
        text: 'Une erreur est survenue lors du téléchargement des images'
      });
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
    }
  }

  async uploadSingleImage(file: File): Promise<void> {
    try {
      const folder = 'biens-immobiliers'; // Customize folder name
      const response = await this.awsService.uploadFile(file, folder).toPromise();

      if (response.success && response.urls && response.urls.length > 0) {
        const uploadedUrl = response.urls[0];
        this.allImages.push(uploadedUrl);
        
        console.log(`Image ${file.name} uploaded successfully:`, uploadedUrl);
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error: any) {
      console.error(`Error uploading image ${file.name}:`, error);
      throw error;
    }
  }

  // Promo image upload (updated with AWS service)
  selectFileimgPromo(e: any) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // File size validation
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier trop volumineux',
        text: `L'image dépasse 5MB. Taille maximum autorisée: 5MB.`
      });
      return;
    }
    
    this.uploadPromoImage(file);
    e.target.value = ''; // Reset input
  }

  async uploadPromoImage(file: File): Promise<void> {
    this.isUploadingPromo = true;
    
    try {
      // Show loading
      Swal.fire({
        title: 'Téléchargement en cours...',
        html: 'Upload de l\'image promotionnelle',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => Swal.showLoading()
      });

      const folder = 'biens-immobiliers-promo'; // Customize folder name
      const response = await this.awsService.uploadFile(file, folder).toPromise();

      Swal.close();
      
      if (response.success && response.urls && response.urls.length > 0) {
        this.imagePromo = response.urls[0];
        
        Swal.fire({
          icon: "success",
          title: "Image promotionnelle ajoutée",
          text: `${file.name} a été uploadée avec succès`,
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        throw new Error(response.error || 'Upload failed');
      }
      
    } catch (error: any) {
      console.error('Error uploading promo image:', error);
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Échec de l'upload",
        text: `Échec du téléchargement de ${file.name}.`,
      });
    } finally {
      this.isUploadingPromo = false;
    }
  }

  // Image management
  async removeImage(index: number): Promise<void> {
    const imageUrl = this.allImages[index];
    
    const result = await Swal.fire({
      title: 'Supprimer cette image?',
      text: "Cette action est irréversible",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        // Try to delete from AWS
        await this.awsService.deleteFile(imageUrl).toPromise();
        console.log('Image deleted from AWS:', imageUrl);
      } catch (error) {
        console.warn('Could not delete image from AWS:', error);
        // Continue with removal from list even if AWS delete fails
      }
      
      this.allImages.splice(index, 1);
      Swal.fire({
        icon: "success",
        title: "Image supprimée",
        text: "L'image a été supprimée avec succès",
        timer: 1500,
        showConfirmButton: false
      });
    }
  }

  async removePromoImage(): Promise<void> {
    if (!this.imagePromo) return;
    
    const result = await Swal.fire({
      title: 'Supprimer l\'image promotionnelle?',
      text: "Cette action est irréversible",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        // Try to delete from AWS
        await this.awsService.deleteFile(this.imagePromo).toPromise();
        console.log('Promo image deleted from AWS:', this.imagePromo);
      } catch (error) {
        console.warn('Could not delete promo image from AWS:', error);
        // Continue with removal even if AWS delete fails
      }
      
      this.imagePromo = '';
      Swal.fire({
        icon: "success",
        title: "Image supprimée",
        text: "L'image promotionnelle a été supprimée",
        timer: 1500,
        showConfirmButton: false
      });
    }
  }

  moveImageUp(index: number): void {
    if (index > 0) {
      [this.allImages[index], this.allImages[index - 1]] = [this.allImages[index - 1], this.allImages[index]];
    }
  }

  moveImageDown(index: number): void {
    if (index < this.allImages.length - 1) {
      [this.allImages[index], this.allImages[index + 1]] = [this.allImages[index + 1], this.allImages[index]];
    }
  }

  // Validation
  validateYouTubeUrl(url: string): boolean {
    if (!url) return true;
    const youtubePattern = /^(https?\:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return youtubePattern.test(url);
  }

  // Update property
  updateProperty(): void {
    // Check if uploads are in progress
    if (this.isUploading || this.isUploadingPromo) {
      Swal.fire({
        icon: 'warning',
        title: 'Téléchargement en cours',
        text: 'Veuillez attendre la fin du téléchargement des images'
      });
      return;
    }

    // Validation
    if (!this.biens.name || !this.biens.description || !this.selectedAgencyName) {
      Swal.fire({
        icon: 'warning',
        title: 'Champs requis',
        text: 'Veuillez remplir tous les champs obligatoires (Nom, Description, Agence)'
      });
      return;
    }

    if (this.biens.videoUrlBien && !this.validateYouTubeUrl(this.biens.videoUrlBien)) {
      Swal.fire({
        icon: 'error',
        title: 'URL YouTube invalide',
        text: 'Veuillez entrer une URL YouTube valide'
      });
      return;
    }

    if (this.allImages.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Image requise',
        text: 'Veuillez ajouter au moins une image du bien'
      });
      return;
    }

    // Prepare update data
    const updateData = {
      "name": this.biens.name,
      "description": this.biens.description,
      "prix": this.biens.prix,
      "agencyName": this.selectedAgencyName,
      "categoryList": this.biens.categoryList || [],
      "imageUrl": this.allImages.length > 0 ? this.allImages[0] : '',
      "images": this.allImages,
      "promoTitle": this.biens.promoTitle,
      "type": this.biens.type,
      "role": this.biens.role,
      "videoUrlBien": this.biens.videoUrlBien,
      "promoImg": this.imagePromo || '',
      "nomCommercial": this.biens.nomCommercial || '',
      "numCommercial": this.biens.numCommercial || '',
      "emailCommercial": this.biens.emailCommercial || ''
    };

    console.log('Updating property:', updateData);

    Swal.fire({
      title: 'Mise à jour en cours...',
      text: 'Veuillez patienter',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    this.bienImmobService.updateBiensImmobs(this.idBienImmobs, updateData).subscribe(
      (response: any) => {
        Swal.close();
        Swal.fire({
          icon: "success",
          title: "Succès!",
          text: "Le bien immobilier a été mis à jour avec succès",
          confirmButtonText: 'OK'
        }).then(() => {
          this._location.back();
        });
      },
      (error) => {
        Swal.close();
        console.error('Error updating property:', error);
        
        let errorMessage = 'Échec de la mise à jour du bien immobilier';
        if (error.status === 400) {
          errorMessage = 'Données invalides. Veuillez vérifier les informations saisies.';
        } else if (error.status === 401) {
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else if (error.status === 500) {
          errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: errorMessage,
        });
      }
    );
  }

  onCancel(): void {
    Swal.fire({
      title: 'Annuler les modifications?',
      text: 'Les modifications non enregistrées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Continuer l\'édition'
    }).then((result) => {
      if (result.isConfirmed) {
        this._location.back();
      }
    });
  }

  // Optional: Check AWS service health
  async checkAWSServiceHealth(): Promise<void> {
    try {
      const health = await this.awsService.checkHealth().toPromise();
      Swal.fire({
        icon: 'success',
        title: 'Service AWS Connecté',
        html: `
          <div style="text-align: left;">
            <p><strong>Statut:</strong> ${health.status}</p>
            <p><strong>Bucket:</strong> ${health.bucket}</p>
            <p><strong>Région:</strong> ${health.region}</p>
            <p><strong>Dernière vérification:</strong> ${health.timestamp}</p>
          </div>
        `,
        confirmButtonColor: '#3f51b5'
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Service AWS non disponible',
        text: 'Impossible de vérifier la connexion au service AWS'
      });
    }
  }

  // Get file name from URL for display
  getFileNameFromUrl(url: string): string {
    return this.awsService.extractFileName(url);
  }
}